/** @jsx jsx */
import { css, jsx, Global } from '@emotion/react';
import { Fragment } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import get from 'lodash/get';

import Header from './participant-header/participant-header';
import Canvas from './participant-canvas/participant-canvas';
import { ToastRewards } from '../../../researcher/blocks/toast';
import { SURVEYS__SHAPE_ITEM } from '../../../researcher/redux/constants';
import ThemeProvider from '../../../researcher/blocks/theme-provider';
import Preloader from '../../../researcher/blocks/preloader/preloader';
import ErrorPage from '../../../researcher/blocks/error-page/error-page';
import ErrorBoubdary from '../../../researcher/blocks/error-boundary/error-boundary';

import { backgroundCss } from './view.style';
import { participantErrorRegistry } from '../../redux/error-registry';
import FacebookPixel from './facebook-pixel';
import LyticsTracker from './lytics-tracker';
import { withBrandingFeature } from '../../util';
import { getLocale } from '../../../../translations/i18n';

const ParticipantView = ({
  disqualification,
  hasError,
  isDisqualified,
  isSurveyInactive,
  showPreloader,
  themeClass,
  ...rest
}) => {
  const { survey } = rest;
  const titlePrefix = withBrandingFeature(get(survey, 'organizationName'), 'Beag.ly');
  return (
    <ThemeProvider>
      <ErrorBoubdary errorRegistry={participantErrorRegistry}>
        <div className={`participant ${themeClass}`}>
          <Helmet
            htmlAttributes={{
              lang: getLocale(),
            }}
            title={survey && (survey.participantDisplayTitle || survey.title)}
            titleTemplate={`${titlePrefix} - %s`}
            meta={[
              {
                name: 'viewport',
                content: 'width=device-width, initial-scale=1, maximum-scale=1',
              },
            ]}
          />
          {!isDisqualified && !isSurveyInactive && <div css={backgroundCss(hasError)} />}
          {isDisqualified && (
            <div style={{ paddingTop: '3rem', overflow: 'clip' }}>
              <ErrorPage
                {...rest}
                message={disqualification?.message}
                image={disqualification?.image}
              />
            </div>
          )}
          {!isDisqualified && showPreloader && !isSurveyInactive && <Preloader blockLevel />}
          {!isDisqualified && showPreloader && isSurveyInactive && (
            <div style={{ paddingTop: '3rem', overflow: 'clip' }}>
              <ErrorPage {...rest} message="The survey is no longer available." />
            </div>
          )}
          {!isDisqualified && !showPreloader && !isSurveyInactive && (
            <Fragment>
              <Global
                styles={css`
                  body > * {
                    visibility: hidden;
                  }
                  body > #app,
                  body > #ui-modal,
                  body > .sortable-ghoost {
                    visibility: visible;
                  }
                `}
              />
              <Header hasError={hasError} {...rest} />
              <ToastRewards {...rest} />
              <Canvas {...rest} />
            </Fragment>
          )}
          <FacebookPixel {...rest} />
          <LyticsTracker {...rest} />
        </div>
      </ErrorBoubdary>
    </ThemeProvider>
  );
};

ParticipantView.propTypes = {
  disqualification: PropTypes.shape({
    message: PropTypes.string,
    image: PropTypes.string,
  }),
  hasError: PropTypes.bool.isRequired,
  isDisqualified: PropTypes.bool,
  isSurveyInactive: PropTypes.bool.isRequired,
  showPreloader: PropTypes.bool.isRequired,
  survey: SURVEYS__SHAPE_ITEM,
  themeClass: PropTypes.string.isRequired,
};
ParticipantView.defaultProps = {
  disqualification: null,
  isDisqualified: false,
  survey: undefined,
};

export default ParticipantView;

/**
 * @see docs/worklog/maintenance/20093-investigate-facebook-auth-errors/rfc-frontend.md
 */
